import {
  Stack,
  SxProps,
  Theme,
  Tooltip,
  TooltipProps,
  Typography,
} from '@mui/material';
import { MouseEvent, useCallback, useEffect, useState } from 'react';

import { Button, ButtonProps } from '@/components/form/baseInputs/Button';
import { COLORS } from '@/styles/tokens/colors';

import {
  incrementCoachmarkSeenCount,
  shouldShowCoachmark,
} from './coachmarkUtils';

export interface CoachmarkProps {
  /** Emphasized title */
  title?: string;
  /** Body text */
  body: string;
  /** Placement relative to the wrapped component */
  placement?: TooltipProps['placement'];
  /** Button props; same as `ButtonProps`, but omits size, variant, href, and onClick. */
  buttonProps?: Omit<ButtonProps, 'size' | 'variant' | 'onClick' | 'href'>;
  /** Text to display on the button */
  buttonLabel: string;
  /** Component to be wrapped with coachmark; used directly as the popper's
   * anchor element, so it must be a single element that forwards refs. */
  children: JSX.Element;
  /** Whether it should display on first render; note that `isOpen` will override this if present */
  initialOpen?: boolean;
  /** Callback when being closed (eg: for recording that it was seen) */
  onClose: () => void;
  /** Don't show an anchor arrow */
  hideArrow?: boolean;
  /** Forcibly set visibility.  Useful for chaining coachmarks or closing on outside action.
   * If undefined, the coachmark will manage its own visibility.
   */
  isOpen?: boolean;
  /** Unique identifier for this coachmark */
  id: string;
  /** Maximum number of times this coachmark should be shown */
  maxInstances?: number;
  /** Optional `sx` applied to the underlying popper. Useful for adjusting `zIndex`
   * (e.g. to render the coachmark beneath modals). */
  popperSx?: SxProps<Theme>;
}

export function Coachmark({
  title,
  body,
  buttonProps = {},
  buttonLabel,
  placement = 'bottom-start',
  children,
  initialOpen = false,
  onClose: externalOnClose,
  hideArrow = false,
  isOpen: externalOpen,
  id,
  maxInstances = Infinity,
  popperSx,
}: CoachmarkProps) {
  const [isOpen, setOpen] = useState<boolean>(
    () => initialOpen && shouldShowCoachmark(id, maxInstances)
  );

  useEffect(() => {
    if (!isOpen && initialOpen && shouldShowCoachmark(id, maxInstances)) {
      setOpen(true);
      incrementCoachmarkSeenCount(id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialOpen, id, maxInstances]);

  const onClose = useCallback(
    (event?: MouseEvent<HTMLButtonElement>) => {
      // Stop propagation and prevent default so dismissing the coachmark
      // doesn't also trigger click handlers or default actions on the wrapped
      // element (e.g. navigating when the coachmark is anchored to a
      // tab/link rendered as an <a href>).
      event?.stopPropagation();
      event?.preventDefault();
      setOpen(false);
      externalOnClose();
    },
    [externalOnClose]
  );

  // Don't render anything if we've exceeded maxInstances or if it's been seen
  if (!shouldShowCoachmark(id, maxInstances) && externalOpen === undefined) {
    return <>{children}</>;
  }

  return (
    <Tooltip
      arrow={!hideArrow}
      placement={placement}
      open={externalOpen ?? isOpen}
      disableFocusListener
      disableHoverListener
      disableTouchListener
      // Describe (rather than label) the anchor so we don't overwrite its
      // own `aria-label`. With the default labelling behavior and a JSX
      // `title`, MUI Tooltip's cloneElement spreads `aria-label: null` onto
      // the anchor, which then propagates through `IconButton`'s
      // `...buttonProps` spread and erases the explicit
      // `aria-label="Add page"` — breaking `getByLabel('Add page')` in the
      // synthetic tests after the coachmark mounts.
      describeChild
      slotProps={{
        popper: {
          // Render inline (no body portal) so the popper inherits its
          // anchor's stacking context — any unrelated overlay (Modal,
          // Dialog, Drawer) opened in a higher stacking context naturally
          // occludes it (and its backdrop blocks interaction). Fixed
          // positioning lets the popper escape any ancestor
          // `overflow: hidden` (e.g. a tab strip clipping its tabs).
          disablePortal: true,
          // Disable popper.js auto-flip and preventOverflow so the
          // requested `placement` is honored even when the anchor sits
          // near a clipping ancestor's edge (e.g. the Pages panel, which
          // would otherwise either flip the coachmark or shift it inward
          // to stay within the panel). Coachmarks are placed
          // intentionally; we'd rather respect the developer's choice
          // and let the popper extend over neighboring page content.
          popperOptions: {
            strategy: 'fixed',
            modifiers: [
              { name: 'flip', enabled: false },
              { name: 'preventOverflow', enabled: false },
            ],
          },
          // Pin to a small explicit z-index by default (overriding MUI's
          // default 1500 tooltip layer) so we paint just above sibling
          // page content (which typically has `z-index: auto`) within the
          // anchor's stacking context, but stay well below the MUI Modal
          // layer (z=1300) — keeping us occluded by any unrelated overlay.
          // The doubled `&&` selector bumps specificity above the default
          // `.MuiPopper-root` rule. Callers can override with `popperSx`,
          // which is appended after our default and wins for any
          // properties it sets at the same specificity.
          sx: popperSx
            ? [
                { '&&': { zIndex: 1 } },
                ...(Array.isArray(popperSx) ? popperSx : [popperSx]),
              ]
            : { '&&': { zIndex: 1 } },
        },
        tooltip: {
          sx: {
            backgroundColor: COLORS.GRAY[500],
            color: COLORS.PRIMITIVES.WHITE,
            maxWidth: 320,
            // Reset `white-space` because the coachmark renders inline (no
            // portal) and would otherwise inherit `nowrap` from ancestors
            // like NavigationTabs' `commonTabStyle`, blowing the tooltip
            // out to a single-line width.
            whiteSpace: 'normal',
          },
        },
        arrow: {
          sx: { color: COLORS.GRAY[500] },
        },
      }}
      title={
        <Stack sx={{ p: 1.5 }}>
          <Typography
            variant="title-xs"
            sx={{ color: COLORS.TEAL[300], pb: 0.5 }}
          >
            {title}
          </Typography>
          <Typography
            variant="body-xs"
            sx={{ color: COLORS.PRIMITIVES.WHITE, pb: 1.5 }}
          >
            {body}
          </Typography>
          <Stack direction="row" justifyContent="end">
            <Button
              variant="primary"
              size="sm"
              onClick={onClose}
              {...buttonProps}
            >
              {buttonLabel}
            </Button>
          </Stack>
        </Stack>
      }
    >
      {children}
    </Tooltip>
  );
}
